import { alpha, Components, iconButtonClasses, Theme } from '@mui/material';
import { Props } from '../../components/icon-button/types';
import { FigmaColorsType } from '../../shared-types/theme/palette';
import getPrefixedClassName from '../../utils/getPrefixClassNames';

export const customVariantClasses = {
  contained: 'containedIconButton',
  outlined: 'outlinedIconButton',
  ghost: 'ghostIconButton',
} as const;

export const customShapeClasses = {
  fullRounded: 'fullRoundedIconButton',
  squareRounded: 'squareRoundedIconButton',
} as const;

type ReturnType = Record<'MuiIconButton', Components['MuiIconButton']>;

const FULLY_ROUNDED = 9999;

export default function IconButton(theme: Theme): ReturnType {
  return {
    MuiIconButton: {
      defaultProps: {
        size: 'medium',
        color: 'primary',
        disableRipple: true,
        disableFocusRipple: true,
      },
      variants: [
        {
          props: { edge: 'start', size: 'medium' },
          style: { marginLeft: -8 },
        },
        {
          props: { edge: 'start', size: 'small' },
          style: { marginLeft: -5 },
        },
        {
          props: { edge: 'end', size: 'medium' },
          style: { marginRight: -8 },
        },
        {
          props: { edge: 'end', size: 'small' },
          style: { marginRight: -5 },
        },
        {
          props: {
            size: 'xlarge',
          },
          style: {
            width: theme.spacing(12),
            height: theme.spacing(12),
            svg: {
              width: theme.spacing(5),
              height: theme.spacing(5),
            },
            [`&.${iconButtonClasses.root}`]: {
              borderRadius: theme.shape.borderRadius * 2,
            },
          },
        },
        {
          props: {
            size: 'xxlarge',
          },
          style: {
            width: theme.spacing(14),
            height: theme.spacing(14),
            svg: {
              width: theme.spacing(6),
              height: theme.spacing(6),
            },
            [`&.${iconButtonClasses.root}`]: {
              borderRadius: theme.shape.borderRadius * 2.5,
            },
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState: { color, variant }, theme }) => {
          const typedTheme = theme as Theme;
          const typedVariant = variant as Props<'button'>['variant'];
          return {
            ...(typedVariant
              ? {
                  [`&.${getPrefixedClassName(customVariantClasses[typedVariant])}`]:
                    createIconButtonStylesByVariant({
                      variant: variant as Props<'button'>['variant'],
                      color,
                      theme: typedTheme,
                    }),
                }
              : {}),
            [`&.${getPrefixedClassName(customShapeClasses.fullRounded)}`]: {
              borderRadius: FULLY_ROUNDED,
            },
            [`&.${getPrefixedClassName(customShapeClasses.squareRounded)}`]: {
              borderRadius: typedTheme.shape.borderRadius * 2,
            },
            [`&.${iconButtonClasses.sizeSmall}`]: {
              width: typedTheme.spacing(9),
              height: typedTheme.spacing(9),

              svg: {
                width: typedTheme.spacing(5),
                height: typedTheme.spacing(5),
              },
            },
            [`&.${iconButtonClasses.sizeMedium}`]: {
              width: typedTheme.spacing(10),
              height: typedTheme.spacing(10),
              svg: {
                width: typedTheme.spacing(5),
                height: typedTheme.spacing(5),
              },
            },
            [`&.${iconButtonClasses.sizeLarge}`]: {
              width: typedTheme.spacing(11),
              height: typedTheme.spacing(11),
              svg: {
                width: typedTheme.spacing(5),
                height: typedTheme.spacing(5),
              },
            },
          };
        },
      },
    },
  };
}

type GhostType = {
  fg: FigmaColorsType;
  bgHover: FigmaColorsType;
  fgDisabled: FigmaColorsType;
  focusRing: FigmaColorsType;
};

type OutlinedType = {
  fg: FigmaColorsType;
  bg: FigmaColorsType;
  bgHover: FigmaColorsType;
  borderColor: FigmaColorsType;
  fgDisabled: FigmaColorsType;
  focusRing: FigmaColorsType;
};

type ContainedType = {
  fg: FigmaColorsType;
  bg: FigmaColorsType;
  bgHover: FigmaColorsType;
  fgDisabled: FigmaColorsType;
  focusRing: FigmaColorsType;
};

const colors: {
  [key in Props<'button'>['variant']]: {
    [key in NonNullable<Props<'button'>['color']>]:
      | ContainedType
      | OutlinedType
      | GhostType;
  };
} = {
  contained: {
    primary: {
      fg: 'Component colors/Components/Buttons/Primary/button-primary-fg',
      bg: 'Component colors/Components/Buttons/Primary/button-primary-bg',
      bgHover:
        'Component colors/Components/Buttons/Primary/button-primary-bg_hover',
      fgDisabled: 'Colors/Foreground/fg-disabled',
      focusRing: 'Colors/Effects/Focus rings/focus-ring',
    },
    secondary: {
      bg: 'Component colors/Components/Buttons/Secondary/button-secondary-bg',
      fg: 'Component colors/Utility/Gray/utility-gray-700',
      bgHover:
        'Component colors/Components/Buttons/Secondary/button-secondary-bg_hover',
      fgDisabled: 'Colors/Foreground/fg-disabled',
      focusRing: 'Colors/Effects/Focus rings/focus-ring',
    },
    error: {
      fg: 'Colors/Foreground/fg-white',
      bg: 'Component colors/Components/Buttons/Primary error/button-primary-error-bg',
      bgHover:
        'Component colors/Components/Buttons/Primary error/button-primary-error-bg_hover',
      fgDisabled: 'Colors/Foreground/fg-disabled',
      focusRing: 'Colors/Effects/Focus rings/focus-ring-error',
    },
  },
  outlined: {
    primary: {
      fg: 'Component colors/Components/Buttons/Tertiary color/button-tertiary-color-fg',
      bgHover:
        'Component colors/Components/Buttons/Secondary color/button-secondary-color-bg_hover',
      borderColor:
        'Component colors/Components/Buttons/Secondary color/button-secondary-color-border',
      fgDisabled: 'Colors/Foreground/fg-disabled',
      focusRing: 'Colors/Effects/Focus rings/focus-ring',
    },
    secondary: {
      fg: 'Component colors/Utility/Gray/utility-gray-700',
      bgHover:
        'Component colors/Components/Buttons/Secondary/button-secondary-bg_hover',
      bg: 'Component colors/Components/Buttons/Secondary/button-secondary-bg',
      borderColor:
        'Component colors/Components/Buttons/Secondary/button-secondary-border',
      fgDisabled: 'Colors/Foreground/fg-disabled',
      focusRing: 'Colors/Effects/Focus rings/focus-ring',
    },
    error: {
      fg: 'Component colors/Components/Buttons/Secondary error/button-secondary-error-fg',
      bgHover:
        'Component colors/Components/Buttons/Secondary error/button-secondary-error-border_hover',
      borderColor:
        'Component colors/Components/Buttons/Secondary error/button-secondary-error-border_hover',
      fgDisabled: 'Colors/Foreground/fg-disabled',
      focusRing: 'Colors/Effects/Focus rings/focus-ring-error',
    },
  },
  ghost: {
    primary: {
      fg: 'Component colors/Components/Buttons/Tertiary color/button-tertiary-color-fg',
      bgHover:
        'Component colors/Components/Buttons/Tertiary color/button-tertiary-color-bg_hover',
      fgDisabled: 'Colors/Foreground/fg-disabled',
      focusRing: 'Colors/Effects/Focus rings/focus-ring',
    },
    secondary: {
      fg: 'Component colors/Utility/Gray/utility-gray-700',
      bgHover:
        'Component colors/Components/Buttons/Secondary/button-secondary-bg_hover',
      fgDisabled: 'Colors/Foreground/fg-disabled',
      focusRing: 'Colors/Effects/Focus rings/focus-ring',
    },
    error: {
      fg: 'Component colors/Components/Buttons/Secondary error/button-secondary-error-fg',
      bgHover:
        'Component colors/Components/Buttons/Tertiary error/button-tertiary-error-bg_hover',
      fgDisabled: 'Colors/Foreground/fg-disabled',
      focusRing: 'Colors/Effects/Focus rings/focus-ring-error',
    },
  },
};

function createIconButtonStylesByVariant({
  variant = 'contained',
  color = 'primary',
  theme,
}: {
  variant: Props<'button'>['variant'];
  color: Props<'button'>['color'];
  theme: Theme;
}) {
  if (variant === 'contained' && 'bg' in colors[variant][color]) {
    const colorKey = colors[variant][color];
    return {
      '&, svg': {
        color: theme.palette[colorKey.fg],
      },
      '&::before': {
        content: '""',
        width: '100%',
        height: '100%',
        position: 'absolute',
        borderRadius: 'inherit',
        boxShadow: `inset 0px 0px 1px 2px ${alpha(theme.palette['Colors/Effects/Shadows/shadow-xs'], 0.02)}`,
      },
      backgroundColor: theme.palette[colorKey.bg],
      '&:hover': {
        backgroundColor: theme.palette[colorKey.bgHover],
      },
      [`&.${iconButtonClasses.disabled}`]: {
        backgroundColor: theme.palette.background.disabled,
        border: `1px solid ${theme.palette.border.disabledSubtle}`,
        boxShadow: `0 1px 2px 0 ${alpha(theme.palette['Colors/Effects/Shadows/shadow-xs'], 0.05)}`,
        svg: {
          color: theme.palette[colorKey.fgDisabled],
        },
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.palette[colorKey.focusRing]}`,
        outlineOffset: '2px',
      },
    };
  }

  if (
    variant === 'ghost' &&
    !('borderColor' in colors[variant][color]) &&
    !('bg' in colors[variant][color])
  ) {
    const colorKey = colors[variant][color];
    return {
      '&, svg': {
        color: theme.palette[colorKey.fg],
      },
      '&:hover': {
        backgroundColor: theme.palette[colorKey.bgHover],
      },
      [`&.${iconButtonClasses.disabled}`]: {
        svg: {
          color: theme.palette[colorKey.fgDisabled],
        },
      },
      '&:focus-visible': {
        outline: `2px solid ${theme.palette[colorKey.focusRing]}`,
        outlineOffset: '2px',
      },
    };
  }

  const colorKey = colors[variant][color] as OutlinedType;

  return {
    '&, svg': {
      color: theme.palette[colorKey.fg],
    },
    '&:hover': {
      backgroundColor: theme.palette[colorKey.bgHover],
    },
    backgroundColor: theme.palette[colorKey.bg],
    border: `1px solid ${theme.palette[colorKey.borderColor]}`,
    boxShadow: `
        0px 1px 2px 0px ${alpha(theme.palette['Colors/Effects/Shadows/shadow-xs'], 0.05)},
        0px -2px 0px 0px ${alpha(theme.palette['Colors/Effects/Shadows/shadow-skeumorphic-inner'], 0.05)} inset,
        0px 0px 0px 0px ${alpha(theme.palette['Colors/Effects/Shadows/shadow-skeumorphic-inner-border'], 0.18)} inset
      `,
    [`&.${iconButtonClasses.disabled}`]: {
      backgroundColor: theme.palette['Colors/Background/bg-primary'],
      border: `1px solid ${theme.palette['Colors/Border/border-disabled_subtle']}`,
      boxShadow: `0 1px 2px 0 ${alpha(theme.palette['Colors/Effects/Shadows/shadow-xs'], 0.05)}`,
      svg: {
        color: theme.palette[colorKey.fgDisabled],
      },
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette[colorKey.focusRing]}`,
      outlineOffset: '2px',
    },
  };
}
